import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { JumlahTanggungan } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { Switch } from "@/components/ui/switch"

const postData = async (newData: JumlahTanggungan) => {
  const response = await api.post("/master/jumlah-tanggungan/store", newData)
  return response.data
}

const patchData = async (newData: JumlahTanggungan) => {
  const response = await api.patch("/master/jumlah-tanggungan/update", newData)
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_jumlah_tanggungan: string
}) => {
  const response = await api({
    url: "/master/jumlah-tanggungan/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: JumlahTanggungan | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<JumlahTanggungan>({
    id_ref_jumlah_tanggungan: selectedRow?.id_ref_jumlah_tanggungan || "",
    jumlah_tanggungan_text: selectedRow?.jumlah_tanggungan_text || "",
    skor_jumlah_tanggungan: selectedRow?.skor_jumlah_tanggungan || 0,
    is_aktif: selectedRow?.is_aktif ?? true,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJumlahTanggungan"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jumlah Tanggungan"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.jumlah_tanggungan_text}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Luas Aset" : "Update Data Luas Aset"}
      />

      <div>
        <Label>Jumlah Tanggungan (text)</Label>
        <Input
          placeholder="Ketik jumlah tanggungan (contoh: 1 orang, 2 orang, >=5 orang)..."
          value={form.jumlah_tanggungan_text}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              jumlah_tanggungan_text: event.target.value,
            }))
          }
        />
      </div>

      <div className="flex-1">
        <Label>Skor Jumlah Tanggungan</Label>
        <Input
          type="number"
          placeholder="Ketik skor jumlah tanggungan..."
          value={form.skor_jumlah_tanggungan}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              skor_jumlah_tanggungan: Number(event.target.value),
            }))
          }
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="status"
          checked={form.is_aktif}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_aktif: !prev.is_aktif,
            }))
          }
        />
        <Label htmlFor="status">Status (Nonaktif/Aktif)</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
